import { Controller } from "@hotwired/stimulus"

// Mine/theirs alignment + auto-scroll-to-bottom for conversations/show.
//
// Mine/theirs: server can't decide at render time because Turbo Stream
// broadcasts render the partial once, outside any request context — Current
// .user is nil there. So we read data-messages-current-user-id-value off the
// container and stamp .message-mine on every .message[data-message-sender-id]
// that matches. A MutationObserver re-runs on newly-appended bubbles.
//
// Auto-scroll: chat apps land you at the bottom on initial load and follow
// new messages — but only when you're already there. Scrolled-up readers
// don't get yanked back. We track "was the user at bottom before this
// mutation?" via a scroll listener, and if so, scroll after the mutation.
//
// Re-sync: the live thread is fed by Turbo Stream broadcasts over ActionCable
// (Message#after_create_commit). On an installed PWA / mobile browser the
// WebSocket drops while the app is backgrounded or idle, and broadcasts sent
// during that gap are NEVER replayed on reconnect — so an inbound SMS reply
// would only appear after a full re-navigation. To self-heal we re-fetch the
// thread (a) whenever the app returns to the foreground, and (b) on a gentle
// poll while the page is visible, as a safety net if the socket is dead. The
// WebSocket append stays the fast path; this just guarantees eventual delivery.
const NEAR_BOTTOM_PX = 150
const POLL_MS = 12000

export default class extends Controller {
  static values = { currentUserId: Number }

  connect() {
    this.markMine()
    this.scrollToBottom()
    this.isAtBottom = true
    this.boundScroll = this.handleScroll.bind(this)
    this.element.addEventListener("scroll", this.boundScroll, { passive: true })
    this.observer = new MutationObserver(() => {
      const wasAtBottom = this.isAtBottom
      this.markMine()
      if (wasAtBottom) this.scrollToBottom()
    })
    this.observer.observe(this.element, { childList: true, subtree: false })

    this.boundResync = this.resync.bind(this)
    this.boundVisibility = this.onVisibility.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibility)
    window.addEventListener("focus", this.boundResync)
    this.startPolling()
  }

  disconnect() {
    this.observer?.disconnect()
    this.element.removeEventListener("scroll", this.boundScroll)
    document.removeEventListener("visibilitychange", this.boundVisibility)
    window.removeEventListener("focus", this.boundResync)
    this.stopPolling()
  }

  onVisibility() {
    if (document.visibilityState === "visible") {
      this.startPolling()
      this.resync()
    } else {
      this.stopPolling()
    }
  }

  startPolling() {
    if (this.pollTimer) return
    this.pollTimer = setInterval(() => this.resync(), POLL_MS)
  }

  stopPolling() {
    if (!this.pollTimer) return
    clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  // Re-fetch the current conversation and swap in the latest message list.
  // Bails out (leaving the thread untouched) on any network error or if the
  // response doesn't contain our container — e.g. a redirect to the login page
  // when the session has expired.
  async resync() {
    if (document.visibilityState !== "visible") return
    if (this.resyncing) return
    this.resyncing = true
    try {
      const response = await fetch(window.location.href, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
      })
      if (!response.ok) return
      const doc = new DOMParser().parseFromString(await response.text(), "text/html")
      const fresh = doc.getElementById(this.element.id)
      if (!fresh || fresh.innerHTML === this.element.innerHTML) return
      const wasAtBottom = this.isAtBottom
      this.element.innerHTML = fresh.innerHTML
      this.markMine()
      if (wasAtBottom) this.scrollToBottom()
    } catch (_e) {
      // Transient network failure — keep the messages we already have.
    } finally {
      this.resyncing = false
    }
  }

  markMine() {
    const me = this.currentUserIdValue
    if (!me) return
    this.element.querySelectorAll(".message:not(.message-mine)").forEach((el) => {
      if (parseInt(el.dataset.messageSenderId, 10) === me) {
        el.classList.add("message-mine")
      }
    })
  }

  handleScroll() {
    const el = this.element
    this.isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
  }

  scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight
    this.isAtBottom = true
  }
}
